<template>
  <div class="privacy-container">
    <div class="privacy-header">
      <h1 class="privacy-title">aKinder™Volunteer</h1>
      <h2 class="privacy-subtitle">Child Safety Standards Policy</h2>
      <p class="privacy-description">
        Type of Application: Volunteering website
      </p>
      <p class="privacy-description">
        Effective date: 1st day of January, 2025
      </p>
    </div>

    <div class="privacy-content">
      <div class="privacy-intro">
        <p>
          aKinder™Volunteer (the "Site") is owned and operated by
          aKinder™Company and Legacy Response has a limited license to use
          software within their mission to create community. AKINDer Company can
          be contacted at:
        </p>
        <ul class="contact-list">
          <li><span>Email: dblais@akindercompany.com</span></li>
          <li><span>Phone: (508) 202-3165</span></li>
          <li>
            <span
              >Address: 399 Boylston Street, 6th Floor, Boston, MA 02116</span
            >
          </li>
        </ul>
      </div>

      <div class="zero-tolerance-banner">
        🛡️ ZERO TOLERANCE POLICY: We maintain strict standards against Child Sexual Abuse and Exploitation (CSAE)
      </div>

      <div
        v-for="(section, index) in sections"
        :key="index"
        class="privacy-section"
      >
        <h2 class="section-title">{{ section.title }}</h2>
        <div class="section-content" v-html="section.content"></div>

        <div v-if="section.subsections" class="subsections">
          <div
            v-for="(subsection, subIndex) in section.subsections"
            :key="subIndex"
            class="subsection"
          >
            <h3 class="subsection-title">{{ subsection.title }}</h3>
            <div class="subsection-content" v-html="subsection.content"></div>
          </div>
        </div>
      </div>

      <div class="privacy-contact">
        <h2 class="section-title">Child Safety Point of Contact</h2>
        <p>
          Our designated child safety officer is empowered to discuss enforcement procedures, coordinate with law enforcement, and take immediate action on reported violations:
        </p>
        <div class="contact-info">
          <p><strong>Primary Contact:</strong></p>
          <p>David Blais - Child Safety Officer</p>
          <p>childsafety@akindercompany.com</p>
          <p>(508) 202-3165</p>
          <p>399 Boylston Street, 6th Floor, Boston, MA 02116</p>
        </div>
      </div>

      <div class="privacy-footer">
        <p>
          <strong>This policy applies to:</strong> aKinder™Volunteer App<br>
          <strong>Developer:</strong> aKinder™Company<br>
          <strong>Contact:</strong> childsafety@akindercompany.com
        </p>
        <br>
        <p>
          <em>This document is publicly accessible and serves as our published standards against Child Sexual Abuse and Exploitation (CSAE) in compliance with Google Play's Child Safety Standards policy.</em>
        </p>
        <br>
        <p>
          © {{ new Date().getFullYear() }} aKinder™Company. All rights reserved.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ChildSafetyPolicy",
  data() {
    return {
      sections: [
        {
          title: "Introduction",
          content: `aKinder™Volunteer is committed to creating a safe and secure environment for all users, with a particular focus on protecting children from sexual abuse and exploitation. We maintain a zero-tolerance policy regarding Child Sexual Abuse and Exploitation (CSAE) and Child Sexual Abuse Material (CSAM).
            <br><br>
            This policy outlines our comprehensive approach to preventing, detecting, and addressing any content or behavior that could harm minors on our platform.`
        },
        {
          title: "Definitions",
          content: `<strong>Child Sexual Abuse and Exploitation (CSAE)</strong> refers to any content or behavior that sexually exploits, abuses, or endangers children. This includes, but is not limited to:
            <div class="list-item">Grooming a child for sexual exploitation</div>
            <div class="list-item">Sextorting a child</div>
            <div class="list-item">Trafficking of a child for sexual purposes</div>
            <div class="list-item">Any other form of sexual exploitation of a minor</div>
            <br>
            <strong>Child Sexual Abuse Material (CSAM)</strong> consists of any visual depiction, including photos, videos, illustrations, or computer-generated imagery, involving the use of a minor engaging in sexually explicit conduct.
            <br><br>
            <strong>Minor</strong> refers to any person under the age of 18, or under the age of majority in their jurisdiction, whichever is higher.`
        },
        {
          title: "Zero-Tolerance Policy",
          content: `aKinder™Volunteer strictly prohibits and will take immediate action against:`,
          subsections: [
            {
              title: "Prohibited Content",
              content: `<div class="list-item">Any visual, audio, or textual content that depicts, promotes, or facilitates child sexual abuse or exploitation</div>
                <div class="list-item">Child Sexual Abuse Material (CSAM) in any form, including digitally generated or AI-manipulated content</div>
                <div class="list-item">Content that sexualizes, grooms, or endangers minors</div>
                <div class="list-item">Any content that could be used to identify, contact, or exploit children</div>`
            },
            {
              title: "Prohibited Behavior",
              content: `<div class="list-item">Grooming or soliciting minors for sexual purposes</div>
                <div class="list-item">Attempting to establish inappropriate relationships with minors</div>
                <div class="list-item">Sharing or requesting CSAM</div>
                <div class="list-item">Using the platform to facilitate child trafficking or exploitation</div>
                <div class="list-item">Any predatory behavior targeting minors</div>`
            }
          ]
        },
        {
          title: "Detection and Prevention Measures",
          subsections: [
            {
              title: "Automated Systems",
              content: `<div class="list-item">Advanced AI-powered content moderation tools to detect potential CSAE content</div>
                <div class="list-item">Automated scanning of user-generated content, including text, images, and videos</div>
                <div class="list-item">Real-time monitoring of user interactions and communications</div>`
            },
            {
              title: "Human Review",
              content: `<div class="list-item">Expert human moderators trained in child safety protocols</div>
                <div class="list-item">Manual review of flagged content and user reports</div>
                <div class="list-item">Regular audits of our detection systems and processes</div>`
            },
            {
              title: "User Verification",
              content: `<div class="list-item">Age verification processes to ensure compliance with age restrictions</div>
                <div class="list-item">Enhanced verification for users interacting with youth-focused volunteer opportunities</div>`
            }
          ]
        },
        {
          title: "Reporting Mechanisms",
          subsections: [
            {
              title: "In-App Reporting",
              content: `Users can report suspected CSAE content or behavior directly through our in-app reporting system:
                <div class="list-item">Dedicated "Report Safety Concern" button accessible throughout the app</div>
                <div class="list-item">Priority processing for child safety reports</div>
                <div class="list-item">Anonymous reporting options available</div>`
            },
            {
              title: "Emergency Contact Information",
              content: `For urgent child safety concerns, contact our Child Safety Team immediately:
                <div class="emergency-contact">
                  <strong>🚨 EMERGENCY CHILD SAFETY CONTACTS</strong><br>
                  <strong>Email:</strong> childsafety@akindercompany.com<br>
                  <strong>Phone:</strong> (508) 202-3165<br>
                  <strong>Child Safety Officer:</strong> David Blais
                </div>`
            }
          ]
        },
        {
          title: "Response Procedures",
          content: `When CSAE content or behavior is identified, we will:`,
          subsections: [
            {
              title: "Immediate Actions",
              content: `<div class="list-item"><strong>Remove Content:</strong> Immediately remove any CSAE content from our platform</div>
                <div class="list-item"><strong>Suspend Accounts:</strong> Permanently ban users involved in CSAE activities</div>
                <div class="list-item"><strong>Preserve Evidence:</strong> Secure evidence for law enforcement while protecting user privacy for non-CSAE related data</div>
                <div class="list-item"><strong>Document Incidents:</strong> Maintain detailed records of violations and responses</div>`
            },
            {
              title: "Law Enforcement Cooperation",
              content: `<div class="list-item">Report confirmed CSAM to the National Center for Missing & Exploited Children (NCMEC) via CyberTipline</div>
                <div class="list-item">Cooperate fully with law enforcement investigations</div>
                <div class="list-item">Report to international authorities when applicable (e.g., INTERPOL, local law enforcement)</div>
                <div class="list-item">Provide necessary evidence and documentation to support investigations</div>`
            }
          ]
        },
        {
          title: "User Safety Features",
          subsections: [
            {
              title: "For Minors",
              content: `<div class="list-item">Enhanced privacy settings for users under 18</div>
                <div class="list-item">Restricted messaging capabilities for minor users</div>
                <div class="list-item">Parental consent requirements for users under 13</div>
                <div class="list-item">Special protections for youth volunteers</div>`
            },
            {
              title: "For All Users",
              content: `<div class="list-item">Comprehensive privacy controls</div>
                <div class="list-item">Block and report functionality</div>
                <div class="list-item">Safety tips and educational resources</div>
                <div class="list-item">Community guidelines clearly outlining acceptable behavior</div>`
            }
          ]
        },
        {
          title: "Staff Training and Education",
          subsections: [
            {
              title: "Team Training",
              content: `<div class="list-item">Mandatory child safety training for all staff and moderators</div>
                <div class="list-item">Regular updates on CSAE detection and response procedures</div>
                <div class="list-item">Collaboration with child safety experts and organizations</div>
                <div class="list-item">Annual training renewals and assessments</div>`
            },
            {
              title: "User Education",
              content: `<div class="list-item">Safety guidelines and resources accessible within the app</div>
                <div class="list-item">Educational content about online safety and recognizing predatory behavior</div>
                <div class="list-item">Clear community standards and consequences for violations</div>
                <div class="list-item">Resources for parents and guardians</div>`
            }
          ]
        },
        {
          title: "Legal Compliance",
          content: `aKinder™Volunteer complies with all applicable laws and regulations, including:
            <div class="list-item">Children's Online Privacy Protection Act (COPPA)</div>
            <div class="list-item">General Data Protection Regulation (GDPR)</div>
            <div class="list-item">Local and international child protection laws</div>
            <div class="list-item">Industry best practices and standards</div>`
        },
        {
          title: "Partnerships and Collaboration",
          content: `We actively collaborate with:
            <div class="list-item">Child safety organizations and advocacy groups</div>
            <div class="list-item">Law enforcement agencies</div>
            <div class="list-item">Technology Coalition and other industry partners</div>
            <div class="list-item">Academic institutions researching child online safety</div>`
        },
        {
          title: "External Resources and Support",
          subsections: [
            {
              title: "For Users",
              content: `<div class="list-item"><strong>Crisis Text Line:</strong> Text HOME to 741741</div>
                <div class="list-item"><strong>National Child Abuse Hotline:</strong> 1-800-4-A-CHILD (1-800-422-4453)</div>
                <div class="list-item"><strong>NCMEC CyberTipline:</strong> https://report.cybertip.org</div>`
            },
            {
              title: "For Parents and Guardians",
              content: `<div class="list-item">Online safety resources and guides available on our website</div>
                <div class="list-item">Tools for monitoring and protecting children online</div>
                <div class="list-item">Communication tips for discussing online safety with children</div>`
            }
          ]
        },
        {
          title: "Transparency and Accountability",
          content: `We are committed to transparency in our child safety efforts through:
            <div class="list-item">Regular transparency reports on our safety measures and enforcement actions</div>
            <div class="list-item">Participation in industry initiatives and working groups</div>
            <div class="list-item">Continuous improvement of our policies and procedures based on best practices</div>
            <div class="list-item">Open communication with users, parents, and safety advocates</div>`
        },
        {
          title: "Policy Updates and Review",
          content: `This policy is reviewed and updated:
            <div class="list-item">At least annually</div>
            <div class="list-item">In response to changes in laws or regulations</div>
            <div class="list-item">Following significant incidents or security events</div>
            <div class="list-item">Based on feedback from child safety experts and law enforcement</div>
            <br>
            When we update this Child Safety Standards Policy, we will update the "Effective Date" at the top of this policy. We recommend that users periodically review our policy to ensure they are notified of any updates.`
        }
      ]
    };
  }
};
</script>

<style scoped>
:root {
  --primary-color: #4a90e2;
  --secondary-color: #5cb85c;
  --accent-color: #f8f9fa;
  --text-color: #333;
  --border-color: #e0e0e0;
}

.privacy-container {
  max-width: 1000px;
  margin: 0 auto;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  color: var(--text-color);
  padding: 20px;
}

.privacy-header {
  text-align: center;
  padding: 2rem 1rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #4a90e2, #5cb85c);
  color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.privacy-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.privacy-subtitle {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.privacy-description {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0.25rem 0;
}

.privacy-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 2rem;
}

.privacy-intro {
  margin-bottom: 2rem;
  font-size: 1rem;
}

.contact-list {
  list-style-type: none;
  padding: 1rem;
  margin: 1rem 0;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.contact-list li {
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
}

.contact-list li::before {
  content: "•";
  color: var(--primary-color);
  font-weight: bold;
  margin-right: 0.75rem;
}

.zero-tolerance-banner {
  background: linear-gradient(135deg, #d9534f, #c9302c);
  color: white;
  padding: 1rem;
  border-radius: 6px;
  margin: 1rem 0 2rem 0;
  text-align: center;
  font-weight: 600;
  font-size: 1.1rem;
}

.privacy-section {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.section-content {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.subsections {
  margin-top: 1rem;
}

.subsection {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.subsection-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #2c3e50;
}

.subsection-content {
  font-size: 1rem;
  padding-left: 0.5rem;
}

.list-item {
  padding-left: 1.5rem;
  margin-top: 0.5rem;
  position: relative;
}

.list-item::before {
  content: "•";
  color: var(--primary-color);
  position: absolute;
  left: 0.5rem;
}

.ml-3 {
  padding-left: 1.5rem;
  display: block;
  margin-top: 0.3rem;
}

.officer-info {
  background-color: #f0f7ff;
  padding: 1rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  line-height: 1.8;
}

.emergency-contact {
  background-color: #ffe6e6;
  border-left: 4px solid #d9534f;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
}

.privacy-contact {
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.contact-info {
  padding: 1.5rem;
  background-color: #f0f7ff;
  border-radius: 6px;
  margin-top: 1rem;
  line-height: 1.8;
}

.privacy-footer {
  text-align: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  font-size: 0.9rem;
  color: #6c757d;
}

@media (max-width: 768px) {
  .privacy-container {
    padding: 10px;
  }

  .privacy-header {
    padding: 1.5rem 1rem;
  }

  .privacy-title {
    font-size: 1.5rem;
  }

  .privacy-subtitle {
    font-size: 1.3rem;
  }

  .privacy-content {
    padding: 1.5rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .zero-tolerance-banner {
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .privacy-header {
    padding: 1.2rem 0.8rem;
  }

  .privacy-title {
    font-size: 1.3rem;
  }

  .privacy-subtitle {
    font-size: 1.1rem;
  }

  .privacy-content {
    padding: 1rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .subsection-title {
    font-size: 0.95rem;
  }

  .zero-tolerance-banner {
    font-size: 0.9rem;
    padding: 0.8rem;
  }
}
</style>